import React, { useState, useEffect } from "react";
import useApiGet from "../hooks/getData";
import useApiPost from "../hooks/postData";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Searchbar from "../../components/Search";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

const emptyForm = {
  id: null,
  duration_sec: "",
  label: "",
  is_active: true,
  sort_order: "",
};

function BattleDurationList() {
  const { getData } = useApiGet();
  const { postData, loading: saving } = useApiPost();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [durations, setDurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const fetchDurations = async () => {
    setLoading(true);
    try {
      const res = await getData("/battles/durations?all=1");
      setDurations(res?.data?.durations || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load battle durations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDurations();
    // eslint-disable-next-line
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setIsEdit(false);
  };

  const openAddForm = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditForm = (d) => {
    setForm({
      id: d.id,
      duration_sec: d.duration_sec || "",
      label: d.label || "",
      is_active: !!d.is_active,
      sort_order: d.sort_order || "",
    });
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.duration_sec) {
      toast.error("Please enter duration in seconds.");
      return;
    }

    const payload = {
      duration_sec: Number(form.duration_sec),
      label: form.label || null,
      is_active: form.is_active ? 1 : 0,
      sort_order: Number(form.sort_order) || 0,
    };

    try {
      const endpoint = isEdit ? "/battles/durations/edit" : "/battles/durations/create";
      const finalPayload = isEdit ? { id: form.id, ...payload } : payload;

      const res = await postData(endpoint, finalPayload);

      if (res?.success) {
        toast.success(res.message || "Saved successfully!");
        setModalOpen(false);
        resetForm();
        fetchDurations();
      } else {
        toast.error(res?.message || "Something went wrong.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save.");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await postData("/battles/durations/delete", { id: deleteId });
      if (res?.success) {
        toast.success(res.message || "Deleted!");
        setDeleteId(null);
        fetchDurations();
      } else {
        toast.error(res?.message || "Failed to delete.");
      }
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  const formatDuration = (sec) => {
    if (sec % 60 === 0) return `${sec / 60} min`;
    return `${sec} sec`;
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <Searchbar />
      <div className="px-4 pb-10 xl:px-6">

        <div className="flex items-center justify-between py-3">
          <h2 className="text-[#000000] font-semibold font-poppins text-xl pt-3 dark:text-darkText">
            Battle Durations
          </h2>
          <button
            style={{ backgroundColor: "#0D9947" }}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            onClick={openAddForm}
          >
            + Add Duration
          </button>
        </div>

        {loading ? (
          <p className="font-poppins dark:text-darkText">Loading...</p>
        ) : durations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <h2 className="text-sm font-semibold font-poppins text-[#000000] dark:text-darkText">
              Don't have any data to show
            </h2>
          </div>
        ) : (
          <div className="overflow-x-auto border border-opacity-gradient dark:border-[#1F1F1F] rounded-lg">
            <table className="min-w-full text-sm text-left font-poppins">
              <thead style={{ backgroundColor: "#593A99" }} className="text-white">
                <tr>
                  <th className="p-3">ID</th>
                  <th className="p-3">Duration (sec)</th>
                  <th className="p-3">Display</th>
                  <th className="p-3">Label</th>
                  <th className="p-3">Sort Order</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {durations.map((d) => (
                  <tr key={d.id} className="border-t dark:border-[#1F1F1F] dark:text-darkText">
                    <td className="p-3">{d.id || "-"}</td>
                    <td className="p-3">{d.duration_sec}</td>
                    <td className="p-3">{formatDuration(d.duration_sec)}</td>
                    <td className="p-3">{d.label || "-"}</td>
                    <td className="p-3">{d.sort_order}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full font-poppins ${
                          d.is_active
                            ? "text-[#0D9947] bg-[#0D9947] bg-opacity-15"
                            : "text-[#999999] bg-[#999999] bg-opacity-15"
                        }`}
                      >
                        {d.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-3 flex gap-2">
                      <button
                        onClick={() => openEditForm(d)}
                        disabled={!d.id}
                        className="px-3 py-1.5 text-xs font-poppins text-[#593A99] border border-[#593A99] rounded-lg disabled:opacity-40"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeleteId(d.id)}
                        disabled={!d.id}
                        className="px-3 py-1.5 text-xs font-poppins text-[#D92D20] border border-[#D92D20] rounded-lg disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? "Edit Battle Duration" : "Add Battle Duration"}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3 py-2">
            <div>
              <label className="text-sm font-poppins dark:text-darkText">Duration (seconds)</label>
              <input
                type="number"
                name="duration_sec"
                value={form.duration_sec}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                placeholder="e.g. 120 (= 2 min)"
              />
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">
                Label (optional — auto-generated if left blank)
              </label>
              <input
                type="text"
                name="label"
                value={form.label}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
                placeholder="e.g. Quick Battle"
              />
            </div>

            <div>
              <label className="text-sm font-poppins dark:text-darkText">Sort Order</label>
              <input
                type="number"
                name="sort_order"
                value={form.sort_order}
                onChange={handleChange}
                className="w-full border border-opacity-gradient rounded-lg p-2 dark:bg-transparent dark:text-darkText"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                id="duration_is_active"
              />
              <label htmlFor="duration_is_active" className="text-sm font-poppins dark:text-darkText">
                Active
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-3">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: "#593A99" }}
                className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
              >
                {saving ? "Saving..." : isEdit ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete Battle Duration?</DialogTitle>
        <DialogContent>
          <p className="pb-4 font-poppins dark:text-darkText">
            This action cannot be undone. Are you sure?
          </p>
          <div className="flex justify-end gap-2 pb-2">
            <button
              onClick={() => setDeleteId(null)}
              className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              style={{ backgroundColor: "#D92D20" }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            >
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BattleDurationList;